import type { ConversationPlacement } from '@crowdex/agent-contracts/api';
import type { PullRequestSummary } from '@crowdex/workspace-protocol';
import { basename } from './desktop-paths';
import {
  sourceWorkspaceForThread,
  workspaceReplicaForThread,
} from './desktop-shell/workspace-replicas';
import {
  type ConversationPullRequestBinding,
  type ConversationPullRequestClaim,
  claimedPullRequest,
  withPullRequestBindings,
} from './pull-request';
import type { SidebarProject, SidebarProjectDirectory } from './sidebar-projects';

export type SidebarThread = {
  id: string;
  title: string;
  /** Its project's label, told apart from another project of the same name. */
  project: string;
  /** Its project's identity (`sidebar-projects`): threads group by it, never by label. */
  projectKey?: string;
  runtime?: 'local' | 'worktree' | 'cloud';
  branch?: string | null;
  /** The folder of its worktree, when it runs in one (its row's tooltip names it). */
  worktreeName?: string;
  pullRequest?: PullRequestSummary | null;
  actionRequired?: boolean;
  loading?: boolean;
  unread?: boolean;
  availableOnDevice?: boolean;
  /** Free labels (the plugin that started it); the sidebar filters on them. */
  tags?: readonly string[];
  /** Its workspace, project and parent (the server's, or the linked cloud's). */
  placement?: ConversationPlacement;
  updatedAt: number;
};

const CLOUD_WORKSPACE_PATTERN = /^\/workspace\/[^/]+\/([^/]+\/[^/]+)$/;

export function isCloudRepositoryWorkspace(value: string | null): boolean {
  return value !== null && CLOUD_WORKSPACE_PATTERN.test(value);
}

export function sourceControlRepositoryFullName(value: string | null): string | null {
  if (value === null) return null;
  const workspace = value.match(CLOUD_WORKSPACE_PATTERN)?.[1];
  if (workspace !== undefined) return workspace.replace(/\.git$/, '');
  const ssh = value.match(/^[^@]+@[^:]+:([^/]+\/[^/]+?)(?:\.git)?$/i)?.[1];
  if (ssh !== undefined) return ssh.replace(/\.git$/, '');
  try {
    const url = new URL(value);
    const fullName = url.pathname.replace(/^\//, '').replace(/\.git$/, '');
    return /^[^/]+\/[^/]+$/.test(fullName) ? fullName : null;
  } catch {
    return null;
  }
}

/** The forge URL of a logical workspace's repository (`repository:<host>/<owner>/<name>`). */
export function logicalWorkspaceRepositoryRemote(
  syncKey: string | null | undefined,
): string | null {
  if (!syncKey) return null;
  const match = syncKey.match(/^repository:([^/]+\/.+\/.+)$/i);
  return match?.[1] === undefined ? null : `https://${match[1].replace(/\.git$/, '')}`;
}

export function logicalWorkspaceRepositoryFullName(
  syncKey: string | null | undefined,
): string | null {
  if (!syncKey) return null;
  const match = syncKey.match(/^repository:[^/]+\/(.+\/.+)$/i);
  return match?.[1]?.replace(/\.git$/, '') ?? null;
}

/**
 * A conversation's pull request is its own: a cloud task's, the one of a worktree of its own's
 * branch, else in a folder other conversations share (the main checkout) the one the server
 * records its work is on (`claimedPullRequest`). That folder's branch alone makes it nobody's.
 */
function ownPullRequest(input: {
  threadId: string;
  runtime: SidebarThread['runtime'];
  folder: string | null;
  claim: ConversationPullRequestClaim | null | undefined;
  /** Its pull requests bound by what it did (`withPullRequestBindings`). */
  bindings: readonly ConversationPullRequestBinding[] | undefined;
  /** The live one of the folder's branch, when known better than the folders' list. */
  live?: PullRequestSummary | null;
  syncedPullRequests: Readonly<Record<string, PullRequestSummary | null>>;
  workspacePullRequests: Readonly<Record<string, PullRequestSummary | null>>;
}): PullRequestSummary | null {
  const cached = input.syncedPullRequests[input.threadId] ?? null;
  if (input.runtime === 'cloud') return cached;
  const live =
    input.live ?? (input.folder === null ? null : input.workspacePullRequests[input.folder]);
  const own =
    input.runtime === 'worktree' && input.folder !== null
      ? (live ?? cached ?? claimedPullRequest(input.claim, null, null))
      : claimedPullRequest(input.claim, live, cached);
  return withPullRequestBindings(own, input.bindings, [live, cached]);
}

export type BuildSidebarThreadsInput = {
  preview: boolean;
  previewThreads: readonly SidebarThread[];
  /** The projects threads are filed under (`buildProjectDirectory`). */
  projects: SidebarProjectDirectory;
  syncedWorkspaces: readonly {
    id: string;
    logicalWorkspaceId?: string;
    rootPath: string;
    name: string;
    sourceRoot?: string | null;
    availableOnDevice?: boolean;
  }[];
  syncedThreads: readonly {
    id: string;
    runtime?: 'local' | 'worktree' | 'cloud';
    workspaceId: string;
    workspaceRoot?: string;
    title: string;
    updatedAt: string;
    lastMessageAt: string | null;
    tags?: readonly string[];
    placement?: ConversationPlacement;
    /** The pull request its work is on, as the server records it. */
    pullRequest?: ConversationPullRequestClaim | null;
    /** Its pull requests bound by what it did, as the server records them. */
    pullRequestBindings?: readonly ConversationPullRequestBinding[];
  }[];
  syncedPullRequests: Readonly<Record<string, PullRequestSummary | null>>;
  workspacePullRequests?: Readonly<Record<string, PullRequestSummary | null>>;
  conversationCache: Readonly<
    Record<
      string,
      {
        id: string;
        title: string;
        runtime?: 'local' | 'worktree' | 'cloud';
        workspaceRoot: string | null;
        messages: readonly unknown[];
        updatedAt: number;
      }
    >
  >;
  chatId: string;
  connectionActionRequired: boolean;
  loadingThreadIds: ReadonlySet<string>;
  /**
   * Conversations whose first message waits to leave (their worktree or cloud sandbox being
   * made): listed, as working, before their transcript has anything.
   */
  waitingThreadIds?: ReadonlySet<string>;
  unreadThreadIds: ReadonlySet<string>;
  messagesLength: number;
  conversationTitle: string;
  workspace: string | null;
  cloudAgentSelected: boolean;
  cloudPullRequest: PullRequestSummary | null;
  executionMode: 'cloud' | 'local' | 'worktree';
  gitStatusBranch: string | null;
  gitStatusPullRequest: PullRequestSummary | null;
  now: number;
};

const NO_THREAD_IDS: ReadonlySet<string> = new Set();

export function buildSidebarThreads(input: BuildSidebarThreadsInput): readonly SidebarThread[] {
  const {
    preview,
    previewThreads,
    projects,
    syncedWorkspaces,
    syncedThreads,
    syncedPullRequests,
    workspacePullRequests = {},
    conversationCache,
    chatId,
    connectionActionRequired,
    loadingThreadIds,
    waitingThreadIds = NO_THREAD_IDS,
    unreadThreadIds,
    messagesLength,
    conversationTitle,
    workspace,
    cloudAgentSelected,
    cloudPullRequest,
    executionMode,
    gitStatusBranch,
    gitStatusPullRequest,
    now,
  } = input;
  if (preview) return previewThreads;
  const filed = (project: SidebarProject) => ({ project: project.label, projectKey: project.key });
  const claims = new Map(syncedThreads.map((entry) => [entry.id, entry.pullRequest] as const));
  const bindings = new Map(
    syncedThreads.map((entry) => [entry.id, entry.pullRequestBindings] as const),
  );
  const pullRequestFor = (
    threadId: string,
    runtime: SidebarThread['runtime'],
    folder: string | null,
    live?: PullRequestSummary | null,
  ) =>
    ownPullRequest({
      threadId,
      runtime,
      folder,
      claim: claims.get(threadId),
      bindings: bindings.get(threadId),
      ...(live === undefined ? {} : { live }),
      syncedPullRequests,
      workspacePullRequests,
    });
  // A cloud conversation runs on the cloud even before its workspace is listed; one of this
  // device runs where its folder is (a worktree of its own, as the server's record of the folder
  // says, or the folder itself).
  const threadRuntime = (
    entry: { runtime?: SidebarThread['runtime'] },
    folder: { rootPath: string; sourceRoot?: string | null } | undefined,
  ): Pick<SidebarThread, 'runtime' | 'worktreeName'> => {
    if (entry.runtime === 'cloud') return { runtime: 'cloud' };
    if (folder === undefined) return {};
    const runtime = entry.runtime ?? (folder.sourceRoot ? 'worktree' : 'local');
    return runtime === 'worktree'
      ? { runtime, worktreeName: basename(folder.rootPath) }
      : { runtime };
  };
  const persisted = syncedThreads.map((entry): SidebarThread => {
    const sourceWorkspace = sourceWorkspaceForThread(entry, syncedWorkspaces);
    // A remote conversation runs where its server keeps it.
    const deviceWorkspace =
      entry.runtime === 'cloud'
        ? sourceWorkspace
        : workspaceReplicaForThread(entry, syncedWorkspaces);
    const rootPath = deviceWorkspace?.rootPath ?? null;
    const runtime = threadRuntime(entry, deviceWorkspace);
    return {
      id: entry.id,
      title: entry.title,
      // A folder that is not on this device still names its project (marked unavailable).
      ...filed(
        projects.ofWorkspace(
          entry.workspaceId,
          sourceWorkspace?.rootPath ?? entry.workspaceRoot ?? null,
        ),
      ),
      ...runtime,
      pullRequest: pullRequestFor(entry.id, runtime.runtime, rootPath),
      ...(entry.runtime !== 'cloud' && deviceWorkspace === undefined
        ? { availableOnDevice: false }
        : {}),
      actionRequired: entry.id === chatId && connectionActionRequired,
      loading: loadingThreadIds.has(entry.id) || waitingThreadIds.has(entry.id),
      unread: unreadThreadIds.has(entry.id),
      ...(entry.tags !== undefined && entry.tags.length > 0 ? { tags: entry.tags } : {}),
      ...(entry.placement === undefined ? {} : { placement: entry.placement }),
      // Local activity remains authoritative while a save is pending, even
      // after navigating away from this conversation.
      updatedAt: Math.max(
        Date.parse(entry.lastMessageAt ?? entry.updatedAt),
        conversationCache[entry.id]?.updatedAt ?? 0,
      ),
    };
  });
  const persistedIds = new Set(persisted.map((entry) => entry.id));
  const locallyCached = Object.values(conversationCache)
    .filter(
      (entry) =>
        (entry.messages.length > 0 || waitingThreadIds.has(entry.id)) &&
        !persistedIds.has(entry.id),
    )
    .map((entry): SidebarThread => {
      const runtime = entry.runtime ?? 'local';
      return {
        id: entry.id,
        title: entry.title,
        ...filed(projects.ofRoot(entry.workspaceRoot)),
        runtime,
        ...(runtime === 'worktree' && entry.workspaceRoot !== null
          ? { worktreeName: basename(entry.workspaceRoot) }
          : {}),
        pullRequest: pullRequestFor(entry.id, runtime, entry.workspaceRoot),
        actionRequired: entry.id === chatId && connectionActionRequired,
        loading: loadingThreadIds.has(entry.id) || waitingThreadIds.has(entry.id),
        unread: unreadThreadIds.has(entry.id),
        updatedAt: entry.updatedAt,
      };
    });
  persisted.push(...locallyCached);
  // A fresh chat is only a local draft until its first message is sent. Keeping
  // it out of the sidebar prevents every app launch (and every reset) from
  // looking like it created a real, empty conversation.
  if (messagesLength === 0 && !persistedIds.has(chatId)) return persisted;
  const currentIndex = persisted.findIndex((entry) => entry.id === chatId);
  const currentTags = persisted[currentIndex]?.tags;
  const persistedCurrent = persisted[currentIndex];
  const currentRuntime: SidebarThread['runtime'] = cloudAgentSelected
    ? 'cloud'
    : executionMode === 'worktree'
      ? 'worktree'
      : 'local';
  const currentThread: SidebarThread = {
    id: chatId,
    title: conversationTitle,
    // Open without a folder (it is not on this device): it stays in its project.
    ...(workspace === null && persistedCurrent !== undefined
      ? {
          project: persistedCurrent.project,
          ...(persistedCurrent.projectKey === undefined
            ? {}
            : { projectKey: persistedCurrent.projectKey }),
        }
      : filed(projects.ofRoot(workspace))),
    ...(workspace === null && persistedCurrent?.availableOnDevice === false
      ? { availableOnDevice: false }
      : {}),
    runtime: currentRuntime,
    ...(currentRuntime === 'worktree' && workspace !== null
      ? { worktreeName: basename(workspace) }
      : {}),
    branch: gitStatusBranch,
    // Its own: the live one of its folder's branch only when that branch is its.
    pullRequest: cloudAgentSelected
      ? (cloudPullRequest ?? pullRequestFor(chatId, 'cloud', null))
      : pullRequestFor(chatId, currentRuntime, workspace, gitStatusPullRequest),
    actionRequired: connectionActionRequired,
    loading: loadingThreadIds.has(chatId) || waitingThreadIds.has(chatId),
    unread: unreadThreadIds.has(chatId),
    ...(currentTags === undefined ? {} : { tags: currentTags }),
    updatedAt:
      currentIndex === -1
        ? (conversationCache[chatId]?.updatedAt ?? now)
        : Math.max(
            persisted[currentIndex]?.updatedAt ?? now,
            conversationCache[chatId]?.updatedAt ?? 0,
          ),
  };
  if (currentIndex === -1) return [currentThread, ...persisted];
  return persisted.map((entry, index) => (index === currentIndex ? currentThread : entry));
}

export type BuildProjectGroupsInput = {
  preview: boolean;
  sidebarThreads: readonly SidebarThread[];
  /** The projects (`buildProjectDirectory`), the same the threads were filed with. */
  projects: SidebarProjectDirectory;
  pinnedProjects: ReadonlySet<string>;
};

/**
 * The threads by project, labelled: one group per project identity (a repository and its
 * worktrees), each project known listed even without threads.
 */
export function buildProjectGroups(input: BuildProjectGroupsInput): [string, SidebarThread[]][] {
  const { preview, sidebarThreads, projects, pinnedProjects } = input;
  const groupedProjects = new Map<string, { label: string; threads: SidebarThread[] }>();
  for (const project of projects.all())
    groupedProjects.set(project.key, { label: project.label, threads: [] });
  if (preview && !groupedProjects.has('label:crowdex'))
    groupedProjects.set('label:crowdex', { label: 'crowdex', threads: [] });
  for (const thread of sortSidebarThreads(sidebarThreads)) {
    const key = thread.projectKey ?? `label:${thread.project}`;
    const group = groupedProjects.get(key) ?? { label: thread.project, threads: [] };
    group.threads.push(thread);
    groupedProjects.set(key, group);
  }
  const groups = [...groupedProjects.values()].map(
    ({ label, threads }): [string, SidebarThread[]] => [label, threads],
  );
  return groups.sort(([leftProject], [rightProject]) => {
    const pinnedDifference =
      Number(pinnedProjects.has(rightProject)) - Number(pinnedProjects.has(leftProject));
    if (pinnedDifference !== 0) return pinnedDifference;
    return leftProject.localeCompare(rightProject);
  });
}

export type BuildProjectPathsInput = {
  preview: boolean;
  projects: SidebarProjectDirectory;
  nativeDesktop: boolean;
};

/**
 * Each project's label and folder: its repository checkout, on this machine's desktop its own
 * before a cloud copy, elsewhere the first one known.
 */
export function buildProjectPaths(input: BuildProjectPathsInput): Map<string, string> {
  const { preview, projects, nativeDesktop } = input;
  const paths = new Map<string, string>();
  for (const project of projects.all()) {
    const path = nativeDesktop
      ? (project.roots.find((root) => !isCloudRepositoryWorkspace(root)) ?? project.roots[0])
      : project.roots[0];
    if (path !== undefined) paths.set(project.label, path);
  }
  if (preview) paths.set('crowdex', '/Users/jonas/Desktop/Crowlingo/crowdex');
  return paths;
}

export function sortSidebarThreads(threads: readonly SidebarThread[]): SidebarThread[] {
  return [...threads].sort((left, right) => {
    const primaryDifference = right.updatedAt - left.updatedAt;
    if (primaryDifference !== 0) return primaryDifference;
    return left.id.localeCompare(right.id);
  });
}

export function formatSidebarAge(updatedAt: number): string {
  // Preview fixtures store their age directly in hours.
  if (updatedAt < 1_000_000_000_000) return `${updatedAt}h`;
  const elapsed = Math.max(0, Date.now() - updatedAt);
  const hours = Math.floor(elapsed / 3_600_000);
  if (hours < 1) return '<1h';
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d`;
  return `${Math.floor(days / 7)}w`;
}

export function formatSidebarUpdatedAt(updatedAt: number): string {
  if (updatedAt < 1_000_000_000_000) {
    return `Updated ${updatedAt} hour${updatedAt === 1 ? '' : 's'} ago`;
  }
  return `Updated ${new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(updatedAt)}`;
}
